import { useState, useEffect } from 'react';
import LocationSelector from '../ui/LocationSelector';
import MapPointPicker from '../ui/MapPointPicker';
import { useLocationDetector } from '@/hooks/useLocationDetector';
import { MapPin, Loader2, Star, Trash2, X, ChevronDown } from 'lucide-react';
import { productApi, FavoriteLocation } from '@/services/api';
import { showNotification } from '@/utils/telegram';
import { t } from '../../i18n/translations';

interface AddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (country: string | null, city: string | null) => void;
  currentCountry?: string | null;
  currentCity?: string | null;
}

// A saved place has a real map point unless it's unset or the (0,0) clear sentinel.
const hasMapPoint = (f: FavoriteLocation): boolean =>
  f.location_lat != null && f.location_lng != null &&
  !(f.location_lat === 0 && f.location_lng === 0);

// Human label for a saved place; appends a short coord id so two places in the
// same city are distinguishable, e.g. "Valencia, ES · 39.47, -0.38".
const placeIdentifier = (f: FavoriteLocation): string => {
  const base = f.label && f.label.trim()
    ? f.label.trim()
    : `${f.city_name}, ${f.country_code}`;
  if (hasMapPoint(f)) {
    return `${base} · ${f.location_lat!.toFixed(2)}, ${f.location_lng!.toFixed(2)}`;
  }
  return base;
};

export default function AddressModal({
  isOpen,
  onClose,
  onSave,
  currentCountry,
  currentCity
}: AddressModalProps) {
  const [country, setCountry] = useState<string | null>(currentCountry || null);
  const [city, setCity] = useState<string | null>(currentCity || null);
  const [saving, setSaving] = useState(false);
  const [favoriteLocations, setFavoriteLocations] = useState<FavoriteLocation[]>([]);
  const [addingToFavorites, setAddingToFavorites] = useState(false);

  // Map coordinates state (for the CURRENT location precise-point section)
  const [mapLat, setMapLat] = useState<number | null>(null);
  const [mapLng, setMapLng] = useState<number | null>(null);
  const [mapRadius, setMapRadius] = useState<number>(5);

  // Collapsible UI state
  const [savedExpanded, setSavedExpanded] = useState(false);
  const [manualExpanded, setManualExpanded] = useState(false);

  // Per-saved-place map point editing (only one open at a time)
  const [openPointId, setOpenPointId] = useState<string | null>(null);
  const [pointDraft, setPointDraft] = useState<{ lat: number | null; lng: number | null; radius: number }>({
    lat: null,
    lng: null,
    radius: 5,
  });
  const [savingPoint, setSavingPoint] = useState(false);

  const { isDetecting, detectedLocation, error: detectionError, detectLocation } = useLocationDetector();

  // Load favorite locations when modal opens
  useEffect(() => {
    if (isOpen) {
      setCountry(currentCountry || null);
      setCity(currentCity || null);
      loadFavoriteLocations();
    }
  }, [isOpen, currentCountry, currentCity]);

  // Handle detected location
  useEffect(() => {
    if (detectedLocation) {
      // Simply set the detected location - user can save or cancel
      setCountry(detectedLocation.countryCode);
      setCity(detectedLocation.cityName);

      // Show success notification
      showNotification(`Location detected: ${detectedLocation.cityName}, ${detectedLocation.countryName}`, 'success');
    }
  }, [detectedLocation]);

  const loadFavoriteLocations = async () => {
    try {
      const data = await productApi.getFavoriteLocations();
      setFavoriteLocations(data.favorite_locations || []);
    } catch (error) {
      console.error('Failed to load favorite locations:', error);
    }
  };

  const handleLocationChange = (newCountry: string | null, newCity: string | null) => {
    setCountry(newCountry);
    setCity(newCity);
  };

  const handleAddToFavorites = async () => {
    if (!country || !city || favoriteLocations.length >= 10) return;

    // Check if already in favorites
    const exists = favoriteLocations.some(
      loc => loc.country_code === country && loc.city_name === city
    );
    if (exists) {
      showNotification('This location is already in your favorites', 'warning');
      return;
    }

    setAddingToFavorites(true);
    try {
      const result = await productApi.addFavoriteLocation({
        country_code: country,
        city_name: city,
        set_as_home: false
      });

      setFavoriteLocations([...favoriteLocations, result.location]);
      showNotification('Added to favorites', 'success');

      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('success');
      }
    } catch (error) {
      console.error('Failed to add to favorites:', error);
      showNotification('Failed to add to favorites', 'error');
    } finally {
      setAddingToFavorites(false);
    }
  };

  const handleSelectFavorite = (favorite: FavoriteLocation) => {
    setCountry(favorite.country_code);
    setCity(favorite.city_name);

    if (window.Telegram?.WebApp?.HapticFeedback) {
      window.Telegram.WebApp.HapticFeedback.selectionChanged();
    }
  };

  const handleRemoveFavorite = async (favoriteId: string) => {
    try {
      await productApi.deleteFavoriteLocation(favoriteId);
      setFavoriteLocations(favoriteLocations.filter(loc => loc.id !== favoriteId));
      showNotification('Removed from favorites', 'success');
    } catch (error) {
      console.error('Failed to remove favorite:', error);
      showNotification('Failed to remove favorite', 'error');
    }
  };

  const handleSetPrimary = async (favoriteId: string) => {
    try {
      const result = await productApi.setPrimaryLocation(favoriteId);

      // Update local state
      setFavoriteLocations(favoriteLocations.map(loc => ({
        ...loc,
        is_primary: loc.id === favoriteId
      })));

      // Update form fields
      setCountry(result.home_country_code);
      setCity(result.home_city_name);

      showNotification('Set as primary location', 'success');
    } catch (error) {
      console.error('Failed to set primary location:', error);
      showNotification('Failed to set primary location', 'error');
    }
  };

  const handleTogglePoint = (favorite: FavoriteLocation) => {
    if (openPointId === favorite.id) {
      setOpenPointId(null);
      return;
    }
    setOpenPointId(favorite.id);
    setPointDraft({
      lat: favorite.location_lat ?? null,
      lng: favorite.location_lng ?? null,
      radius: favorite.location_radius_km ?? 5,
    });
  };

  const handleSavePoint = async (favorite: FavoriteLocation) => {
    setSavingPoint(true);
    try {
      // Missing pin => send (0,0) sentinel so backend clears the stored point.
      const clearing = pointDraft.lat == null || pointDraft.lng == null;
      const result = await productApi.updateFavoriteLocation(
        favorite.id,
        clearing
          ? { location_lat: 0, location_lng: 0 }
          : {
              location_lat: pointDraft.lat,
              location_lng: pointDraft.lng,
              location_radius_km: pointDraft.radius,
            }
      );

      // Reflect the server truth immediately.
      setFavoriteLocations(favoriteLocations.map(loc =>
        loc.id === favorite.id ? result.location : loc
      ));
      setOpenPointId(null);
      showNotification(t('location.pointSaved'), 'success');

      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('success');
      }
    } catch (error) {
      console.error('Failed to save map point:', error);
      showNotification('Failed to save map point', 'error');
    } finally {
      setSavingPoint(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Save location with map coordinates
      await productApi.updateUserLocation({
        home_country_code: country,
        home_city_name: city,
        location_lat: mapLat,
        location_lng: mapLng,
        location_radius_km: mapLat ? mapRadius : undefined,
      });
      await onSave(country, city);

      // Check if this should also be added to favorites AFTER saving
      if (country && city && favoriteLocations.length < 10) {
        const exists = favoriteLocations.some(
          loc => loc.country_code === country && loc.city_name === city
        );

        if (!exists) {
          // Automatically add to favorites when setting home location
          try {
            await productApi.addFavoriteLocation({
              country_code: country,
              city_name: city,
              set_as_home: true
            });
          } catch (error) {
            console.error('Failed to add to favorites:', error);
            // Don't show error for favorites, location is already saved
          }
        }
      }

      onClose();

      // Haptic feedback
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('success');
      }
    } catch (error) {
      console.error('Failed to save location:', error);
      showNotification('Failed to save location', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = () => {
    setCountry(null);
    setCity(null);
  };

  if (!isOpen) return null;

  // The saved place that matches the current selection (if any), used for the
  // collapsed one-line summary of the Saved section.
  const activeFavorite = favoriteLocations.find(
    loc => loc.country_code === country && loc.city_name === city
  );
  const currentSummary = activeFavorite
    ? placeIdentifier(activeFavorite)
    : (country && city ? `${city}, ${country}` : 'No location selected');

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center">
      {/* Backdrop with fade-in animation */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />

      {/* Modal with slide-up animation */}
      <div className="relative w-full sm:max-w-md mx-0 sm:mx-4 bg-white rounded-t-2xl sm:rounded-2xl
        max-h-[85vh] flex flex-col shadow-2xl animate-slide-up
        pb-[env(safe-area-inset-bottom,0px)]">

        {/* Header with gradient */}
        <div className="flex items-center justify-between px-5 py-4 bg-gradient-to-r from-blue-500 to-blue-600
          rounded-t-2xl flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-white">Set Your Location</h2>
            <p className="text-blue-100 text-xs mt-0.5">See products near you</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center
              text-white hover:bg-white/30 transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

          {/* Quick Setup: detect geolocation + toggle manual country/city picker */}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Quick Setup</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={detectLocation}
                disabled={isDetecting}
                className="px-3 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white
                  rounded-xl hover:from-green-600 hover:to-emerald-700
                  disabled:opacity-50 disabled:cursor-not-allowed
                  flex items-center justify-center gap-2 transition-all shadow-md shadow-green-500/20
                  active:scale-[0.98] text-sm font-semibold"
              >
                {isDetecting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <MapPin className="w-4 h-4" />
                )}
                <span className="truncate">{t('location.useMyLocation')}</span>
              </button>
              <button
                onClick={() => setManualExpanded(v => !v)}
                className={`px-3 py-3 rounded-xl border-2 flex items-center justify-center gap-1.5
                  transition-all active:scale-[0.98] text-sm font-semibold ${
                  manualExpanded
                    ? 'border-blue-400 bg-blue-50 text-blue-600'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="truncate">{t('location.manualSelection')}</span>
                <ChevronDown className={`w-4 h-4 flex-shrink-0 transition-transform ${manualExpanded ? 'rotate-180' : ''}`} />
              </button>
            </div>

            {manualExpanded && (
              <div className="mt-2">
                <LocationSelector
                  countryCode={country}
                  cityName={city}
                  onChange={handleLocationChange}
                />
              </div>
            )}

            {detectionError && (
              <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
                {detectionError}
              </div>
            )}
          </div>

          {/* Saved places (collapsible): collapsed shows current selection, expanded shows all */}
          <div>
            <button
              onClick={() => setSavedExpanded(v => !v)}
              className="w-full flex items-center justify-between mb-2"
            >
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                {t('location.savedPlaces')} ({favoriteLocations.length}/10)
              </h3>
              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${savedExpanded ? 'rotate-180' : ''}`} />
            </button>

            {!savedExpanded ? (
              <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-gray-50 border-2 border-transparent">
                {activeFavorite?.is_primary && (
                  <Star className="w-4 h-4 text-yellow-500 fill-current flex-shrink-0" />
                )}
                <MapPin className="w-4 h-4 text-blue-500 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide leading-none">
                    {t('location.currentLabel')}
                  </p>
                  <span className="text-sm font-medium text-gray-800 truncate block">
                    {currentSummary}
                  </span>
                </div>
              </div>
            ) : favoriteLocations.length === 0 ? (
              <p className="text-sm text-gray-400 px-1 py-2">{t('location.noSaved')}</p>
            ) : (
              <div className="space-y-1.5 max-h-72 overflow-y-auto">
                {favoriteLocations.map((favorite) => (
                  <div key={favorite.id}>
                    <div
                      onClick={() => handleSelectFavorite(favorite)}
                      className={`flex items-center justify-between p-2.5 rounded-xl cursor-pointer transition-all
                        active:scale-[0.98] ${
                        favorite.is_primary
                          ? 'bg-blue-50 border-2 border-blue-400'
                          : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-2.5 flex-1 min-w-0">
                        {favorite.is_primary && (
                          <Star className="w-4 h-4 text-yellow-500 fill-current flex-shrink-0" />
                        )}
                        <span className="text-sm font-medium text-gray-800 truncate">
                          {placeIdentifier(favorite)}
                        </span>
                      </div>
                      <div className="flex items-center gap-0.5 flex-shrink-0 ml-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleTogglePoint(favorite); }}
                          className={`p-1.5 rounded-lg hover:bg-white transition-colors ${
                            openPointId === favorite.id
                              ? 'text-blue-500'
                              : hasMapPoint(favorite) ? 'text-blue-400' : 'text-gray-400 hover:text-blue-500'
                          }`}
                          title={t('location.setPoint')}
                        >
                          <MapPin className="w-3.5 h-3.5" />
                        </button>
                        {!favorite.is_primary && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleSetPrimary(favorite.id); }}
                            className="p-1.5 text-gray-400 hover:text-yellow-500 rounded-lg hover:bg-white transition-colors"
                            title="Set as primary"
                          >
                            <Star className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleRemoveFavorite(favorite.id); }}
                          className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-white transition-colors"
                          title="Remove"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Inline per-place map point editor */}
                    {openPointId === favorite.id && (
                      <div className="mt-2 p-2.5 rounded-xl bg-gray-50 border border-gray-200 space-y-2.5">
                        <MapPointPicker
                          lat={pointDraft.lat}
                          lng={pointDraft.lng}
                          radiusKm={pointDraft.radius}
                          onChange={(lat, lng, r) => setPointDraft({ lat, lng, radius: r })}
                          onClear={() => setPointDraft(d => ({ ...d, lat: null, lng: null }))}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => setOpenPointId(null)}
                            className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-xl text-gray-700 text-sm font-semibold
                              hover:bg-white active:scale-[0.98] transition-all"
                          >
                            {t('common.cancel')}
                          </button>
                          <button
                            onClick={() => handleSavePoint(favorite)}
                            disabled={savingPoint}
                            className="flex-1 px-3 py-2 bg-blue-500 text-white rounded-xl text-sm font-semibold
                              hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed
                              active:scale-[0.98] transition-all flex items-center justify-center gap-1.5"
                          >
                            {savingPoint && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                            {t('common.save')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Map point picker */}
          {country && city && (
            <div>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                {t('location.preciseLocation')}
              </h3>
              <MapPointPicker
                lat={mapLat}
                lng={mapLng}
                radiusKm={mapRadius}
                onChange={(lat, lng, r) => {
                  setMapLat(lat)
                  setMapLng(lng)
                  setMapRadius(r)
                }}
                onClear={() => {
                  setMapLat(null)
                  setMapLng(null)
                }}
              />
            </div>
          )}

          {/* Action row */}
          <div className="flex items-center justify-between pt-1">
            {(country || city) ? (
              <button
                onClick={handleClear}
                className="text-sm text-red-500 hover:text-red-600 font-medium transition-colors"
              >
                Clear location
              </button>
            ) : <span />}
            {country && city && favoriteLocations.length < 10 && !favoriteLocations.some(
              loc => loc.country_code === country && loc.city_name === city
            ) && (
              <button
                onClick={handleAddToFavorites}
                disabled={addingToFavorites}
                className="text-sm text-blue-500 hover:text-blue-600 font-medium transition-colors flex items-center gap-1"
              >
                {addingToFavorites ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Star className="w-3.5 h-3.5" />
                )}
                Add to favorites
              </button>
            )}
          </div>
        </div>

        {/* Footer buttons */}
        <div className="flex gap-3 px-5 py-4 border-t border-gray-100 flex-shrink-0 bg-white rounded-b-2xl">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl text-gray-700 font-semibold
              hover:bg-gray-50 active:scale-[0.98] transition-all"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-4 py-3 bg-blue-500 text-white rounded-xl font-semibold
              hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed
              active:scale-[0.98] transition-all shadow-lg shadow-blue-500/20"
          >
            {saving ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving
              </span>
            ) : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
